/**
 * SchedulerSiteEmbed — 현장 하나의 공사스케줄만 다른 화면(SiteWorkspaceDrawer 등) 안에
 * 그대로 끼워 넣기 위한 진입점. SchedulerPage는 라우트 전체를 자기 화면으로 가정하고
 * 있어(브라우저 URL을 직접 바꾸고, 회사 전체 현장 전환 우측 패널을 내장하고, 생성 마법사·
 * 카카오공유·설정 등 이 컨텍스트에 맞지 않는 화면까지 포함) 그대로 마운트하면 위험하다 —
 * 그래서 "스케줄 관리" 뷰 4종(간트/캘린더/리스트/공종수정)만 로컬 state로 전환하며 골라
 * 쓰고, RightPanel(현장 전환)·생성/설정/공유 플로우는 아예 포함하지 않는다.
 *
 * 저장 안 한 변경사항 처리: SchedulerPage의 useBlocker(라우트 이동 차단)는 이 임베드엔
 * 없다 — 여긴 라우트가 바뀌지 않아 애초에 막을 "이동"이 없다. 대신 각 뷰의 onDirtyChange로
 * dirty 여부만 받아 상위(SiteWorkspaceDrawer)가 닫기 전 확인할 수 있게 노출한다.
 */
import { useEffect, useMemo, useState } from 'react'
import { apiClient, useAuthStore, getSiteColor } from '@indipia/shared'
import { EmptyState, ErrorState, Skeleton } from '@indipia/ui-core'
import { CommonCodesProvider } from '../context/CommonCodesContext'
import { SiteRefreshProvider } from '../context/SiteRefreshContext'
import { SchedulerHostProvider, useSchedulerHost } from '../context/SchedulerHostContext'
import { GanttView, type GanttDeepLink } from '../views/GanttView'
import { CalendarScheduleView } from '../views/CalendarScheduleView'
import { ScheduleListView } from '../views/ScheduleListView'
import type { SiteInfo } from '../views/CalendarMultiView'
import type { NavFn, DirtyHandle } from './SchedulerPage'

type EmbedView = 'gantt' | 'cal-b2' | 'work-type'

interface ScheduleSummary {
  scheduleId:     number
  siteId:         number
  siteName:       string
  customerName:   string
  contact:        string
  startDate:      string
  endDate:        string
  scheduleStatus: string
  siteDoneYn?:    string
  siteColor:      string | null
}

export interface SchedulerSiteEmbedProps {
  siteId: number
  /** dirty(저장 안 한 변경사항) 여부가 바뀔 때마다 상위에 알린다 — 드로어 닫기 전 확인용. */
  onDirtyChange?: (dirty: DirtyHandle | null) => void
  /**
   * 화면을 벗어나는 이동(셸 나의 할일·작업보고확인)을 상위가 감싼다 — go 를 받아 드로어를 닫고(저장 안 한 변경사항 확인 포함) 이동한다.
   * 주지 않으면 해당 이동 링크·버튼은 보이지 않는다.
   */
  navigateAway?: (go: () => void) => void
  /**
   * true 면 간트를 신호등 켜짐 + [빨강·노랑만] 기본 ON 으로 연다 — 나의 현장 [진행현황] 진입에서만 준다.
   * 완료현황 탭 라벨·딥링크·메뉴 진입(SchedulerPage)은 주지 않는다(꺼짐 유지).
   */
  signalDefaultOn?: boolean
}

/** [진행현황] 진입의 신호 층 초기값 — 켜짐 + 빨강·노랑만 */
const PROGRESS_SIGNAL_DEFAULTS = { on: true, onlyRY: true } as const

export function SchedulerSiteEmbed({ siteId, onDirtyChange, navigateAway, signalDefaultOn }: SchedulerSiteEmbedProps) {
  const user = useAuthStore(s => s.user)
  // 호스트(셸)가 주입한 이동 콜백을 드로어 닫기와 묶어 다시 주입한다(드로어에서는 이동 시 드로어를 닫는다)
  const baseHost = useSchedulerHost()
  const host = useMemo(() => ({
    ...baseHost,
    openTodo: navigateAway && baseHost?.openTodo ? () => navigateAway(baseHost.openTodo!) : undefined,
    openPath: navigateAway && baseHost?.openPath ? (path: string) => navigateAway(() => baseHost.openPath!(path)) : undefined,
    openWorkOrder: navigateAway && baseHost?.openWorkOrder ? (p?: { siteId?: number | null }) => navigateAway(() => baseHost.openWorkOrder!(p)) : undefined,
  }), [baseHost, navigateAway])
  const [allSites, setAllSites] = useState<SiteInfo[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [view, setView] = useState<EmbedView>('gantt')
  // 리스트 → 간트 상세창 열기(E-03): 임베드는 주소를 쓰지 않으므로 딥링크를 로컬 상태로 들고 간트가 한 번 소비한다
  const [deepLink, setDeepLink] = useState<GanttDeepLink | null>(null)

  useEffect(() => {
    if (!user?.compId) return
    setLoadError(false)
    apiClient
      .get<{ data: { schedules: ScheduleSummary[] } }>('/schedule/schedules', {
        params: { pageSize: 200 },
        headers: { 'X-Comp-Id': String(user.compId) },
      })
      .then(res => {
        const schedules = res.data.data?.schedules ?? []
        setAllSites(schedules.map((s): SiteInfo => ({
          scheduleId:     s.scheduleId,
          siteId:         s.siteId,
          siteName:       s.siteName     ?? '',
          customerName:   s.customerName ?? '',
          contact:        s.contact      ?? '',
          startDate:      s.startDate    ?? '',
          endDate:        s.endDate      ?? '',
          scheduleStatus: s.scheduleStatus ?? '',
          siteDoneYn:     s.siteDoneYn,
          color:          s.siteColor ?? getSiteColor(s.siteId),
          taskCount:      0,
          progress:       0,
        })))
      })
      .catch(() => setLoadError(true))
  }, [user?.compId, reloadKey])

  const selectedSite = allSites?.find(s => s.siteId === siteId)

  // GanttView 등은 SchedulerPage의 전체 view 전환용 NavFn을 기대하지만, 이 임베드가 지원하는
  // 범위는 "스케줄 관리" 4종뿐이다 — 그 밖(생성 마법사·설정·카카오공유 등)의 전환 요청은
  // 이 컨텍스트에 대응 화면이 없으므로 조용히 무시한다(발생 시나리오: 없음 — 4종 내부 상호
  // 전환에만 navigate를 쓴다).
  const navigate: NavFn = (v, _scheduleId, taskId, _initMonth, _resetSiteId, card) => {
    if (v === 'gantt' || v === 'cal-b2' || v === 'work-type') {
      // 임베드는 현장 하나뿐이라 siteId 는 props 값을 쓴다. 간트가 아닌 보기로 가면 남은 딥링크를 버린다.
      setDeepLink(v === 'gantt' && taskId !== undefined ? { siteId, taskId, card: card ?? null, pop: null } : null)
      setView(v)
    }
  }

  if (loadError) {
    return <ErrorState onRetry={() => setReloadKey(k => k + 1)} />
  }
  if (allSites === null) {
    return <Skeleton className="m-4 h-40 rounded-md" />
  }
  if (!selectedSite) {
    return (
      <EmptyState
        title="아직 등록된 공사스케줄이 없습니다"
        description="공사스케줄은 나의 공사스케줄에서 만들 수 있어요."
      />
    )
  }

  return (
    <SchedulerHostProvider value={host}>
    <CommonCodesProvider>
    <SiteRefreshProvider value={() => setReloadKey(k => k + 1)}>
      {view === 'gantt' && (
        <GanttView navigate={navigate} selectedSite={selectedSite} onDirtyChange={onDirtyChange} embedded signalDefaults={signalDefaultOn ? PROGRESS_SIGNAL_DEFAULTS : undefined} deepLink={deepLink} onDeepLinkConsumed={() => setDeepLink(null)} />
      )}
      {view === 'cal-b2' && (
        <CalendarScheduleView navigate={navigate} selectedSite={selectedSite} onDirtyChange={onDirtyChange} embedded />
      )}
      {view === 'work-type' && (
        <ScheduleListView navigate={navigate} selectedSite={selectedSite} embedded />
      )}
    </SiteRefreshProvider>
    </CommonCodesProvider>
    </SchedulerHostProvider>
  )
}
